import * as nodePath from 'node:path';
import type { FileSystemSnapshot } from '~/lib/snapshots/snapshot';
import type { FileMap, UnrestrictedDirent } from '~/lib/stores/files';
import { WORK_DIR } from '~/utils/constants';
import { addWorkDir } from '~/utils/workDir';

export interface Change {
  type: 'mkdir' | 'rmdir' | 'writeFile' | 'removeFile';
  filePath: string;
  dirent: UnrestrictedDirent;
}

export function generateChanges(files: FileMap, snapshot: FileSystemSnapshot) {
  // keep track of all the files that exist on disk
  const currentFiles = new Set(Object.keys(files));

  const changes: Change[] = [];

  for (const [fileName, currentFile] of Object.entries(snapshot)) {
    const filePath = addWorkDir(fileName);

    const oldFile = files[filePath];

    if (oldFile?.type === 'file') {
      delete oldFile.lastModified;
    }

    currentFiles.delete(filePath);

    // we also delete all the parent folders as we should keep them
    let parentDir = nodePath.dirname(filePath);

    while (parentDir !== WORK_DIR) {
      currentFiles.delete(parentDir);

      parentDir = nodePath.dirname(parentDir);
    }

    if (oldFile?.type == 'folder' && currentFile.type === 'file') {
      /**
       * If the old file is a folder and the new file is a file, we need to remove all children from the current
       * files list because they will get removed when calling `rm` on the parent folder.
       */
      for (const file of currentFiles) {
        if (file.startsWith(`${filePath}/`)) {
          currentFiles.delete(file);
        }
      }
    }

    if (oldFile) {
      if (oldFile.type === 'folder') {
        // if the file was a folder, and now it's a file, we need to remove the folder and write the new file
        if (currentFile.type === 'file') {
          changes.push({ type: 'rmdir', filePath, dirent: oldFile });
          changes.push({ type: 'writeFile', filePath, dirent: currentFile });
        }
      } else if (oldFile.type === 'file') {
        if (currentFile.type === 'file') {
          // we only write the file if they are different
          if (oldFile.content !== currentFile.content) {
            changes.push({ type: 'writeFile', filePath, dirent: currentFile });
          }
        } else {
          // if the file was a file, and now it's a folder, we need to remove the file and create the folder
          changes.push({ type: 'removeFile', filePath, dirent: oldFile });
          changes.push({ type: 'mkdir', filePath, dirent: currentFile });
        }
      }
    } else {
      // if there is no old file, we can just create the new file
      if (currentFile.type === 'file') {
        changes.push({ type: 'writeFile', filePath, dirent: currentFile });
      } else {
        changes.push({ type: 'mkdir', filePath, dirent: currentFile });
      }
    }
  }

  // all files that are currently still in the currentFiles set need to be removed as they don't exist anymore
  for (const filePath of currentFiles) {
    const oldFile = files[filePath];

    if (oldFile?.type === 'file') {
      changes.push({ type: 'removeFile', filePath, dirent: oldFile });
    } else if (oldFile?.type === 'folder') {
      changes.push({ type: 'rmdir', filePath, dirent: oldFile });
    }
  }

  return changes;
}
